import { IGenericTableData } from '@/interfaces/powerMarkets/table/IGenericTableData.ts';
import { IGenericTableRowFormatting } from '@/interfaces/powerMarkets/table/IGenericTableRowFormatting.ts';
import { ILiveTablePushChange } from '@/interfaces/powerMarkets/table/ILiveTablePushChange.ts';
import { IGenericTableRowData } from '@/interfaces/powerMarkets/table/IGenericTableRowData.ts';
import { DEFAULT_BID_COLOUR, DEFAULT_OFFER_COLOUR } from '@/utils/balancing/boaHelper.ts';
import { formatGenericTableDateValue } from '@/utils/dateUtils.ts';
import { IPlantGroupColourSettings } from '@/helpers/powerMarkets/plantGroupSettings.ts';
import { IGenericTableHeaderRow, IGenericTableParentHeaderRow } from '@/interfaces/powerMarkets/table/IGenericTableHeaderRow.ts';

export interface ILiveTableDisplaySettings {
    bidColour: string;
    bingoNamesToEnums: Record<string, string>;
    bingoColourOverrides: Record<string, string>;
    offerColour: string;
    useDarkHeatmapColours: boolean;
    selectedPlantGroupColourSettings: IPlantGroupColourSettings;
    selectedPlantIds: Array<string>;
}

type ITableCellWithDefaults = IGenericTableRowFormatting & {
    backgroundColourDefault?: string | null;
};

export function updateTableDataWithPush (
    tableData: IGenericTableData,
    pushData: Array<ILiveTablePushChange>,
    maxEntriesOnTable?: number | null,
): { success: boolean; error?: string } {
    try {
        const success = pushData.every(change => {
            switch (change.changeType) {
                case 'Add':
                    if (!change.row) {
                        throw new Error('Added row is missing');
                    }
                    tableData.data.unshift(change.row);
                    break;
                case 'Edit': {
                    if (!change.row) {
                        throw new Error('Edited row is missing');
                    }
                    const editRow = change.row;
                    if (tableData.data && change.rowIndex < tableData.data.length) {
                        if (tableData && tableData.data && tableData.data[change.rowIndex].uniqueId !== editRow.uniqueId) {
                            throw new Error('Edited row is using an incorrect ID');
                        }

                        Object.keys(editRow.row).forEach(header => {
                            if (tableData && tableData.data) {
                                if (tableData.data) {
                                    tableData.data[change.rowIndex].row[header] = editRow.row[header];
                                }
                            }
                        });
                    } else {
                        throw new Error('Row index is less than table length');
                    }
                    break;
                }
                case 'Delete':
                    tableData.data.splice(change.rowIndex, 1);
                    break;
                case 'DeleteByUniqueId': {
                    if (!change.row) {
                        throw new Error('DeleteByUniqueId row is missing');
                    }
                    const rowToDelete = change.row;
                    const rowIndex = tableData.data.findIndex(row => row.uniqueId === rowToDelete.uniqueId);
                    if (rowIndex !== -1) {
                        tableData.data.splice(rowIndex, 1);
                    }
                    break;
                }
                default:
                    break;
            }
            return true;
        });
        if (success) {
            trimTableDataToMaxEntries(tableData, maxEntriesOnTable);
        }
        return { success };
    } catch (error: unknown) {
        return { success: false, error: error instanceof Error ? error.message : String(error) };
    }
}

function findEarliestIndex (data: IGenericTableData['data'], headerToSort: string): number {
    let earliestIndex = 0;
    let earliestTime = data[0]?.row?.[headerToSort]?.values?.[0] ?? Number.MAX_SAFE_INTEGER;

    data.forEach((item, index) => {
        const currentTime = item?.row?.[headerToSort]?.values?.[0] ?? Number.MAX_SAFE_INTEGER;
        if (currentTime < earliestTime) {
            earliestTime = currentTime;
            earliestIndex = index;
        }
    });

    return earliestIndex;
}

function getStoredBackgroundColour (cell: ITableCellWithDefaults): string | null {
    if (!('backgroundColourDefault' in cell)) {
        cell.backgroundColourDefault = cell.backgroundColour;
    }

    return cell.backgroundColourDefault || cell.backgroundColour;
}

function applyBmFeedOrNotificationsColours (
    tableData: IGenericTableData,
    moduleType: string,
    displaySettings: ILiveTableDisplaySettings,
): void {
    tableData.data.forEach((row) => {
        applyBmFeedOrNotificationsRowColours(row, moduleType, displaySettings);
    });
}

function applyBmFeedOrNotificationsRowColours (
    row: IGenericTableRowData,
    moduleType: string,
    displaySettings: ILiveTableDisplaySettings,
): void {
    const categoryCell = row.row?.Category as ITableCellWithDefaults | undefined;
    if (!categoryCell) return;

    const defaultBackgroundColour = getStoredBackgroundColour(categoryCell);
    const entryName = categoryCell.values?.[0]?.toString() ?? '';
    const enumName = displaySettings.bingoNamesToEnums[entryName];

    if (enumName && Object.prototype.hasOwnProperty.call(displaySettings.bingoColourOverrides, enumName)) {
        categoryCell.backgroundColour = displaySettings.bingoColourOverrides[enumName];
    } else {
        categoryCell.backgroundColour = defaultBackgroundColour;
    }
    categoryCell.textColour = 'black';

    if (moduleType !== 'BmFeed') return;

    Object.values(row.row).forEach((cell) => {
        cell.backgroundColour = categoryCell.backgroundColour;
        cell.textColour = categoryCell.textColour;
    });
}

function applyBoaColours (tableData: IGenericTableData, bidColour: string, offerColour: string): void {
    tableData.data.forEach((row) => {
        applyBoaRowColours(row, bidColour, offerColour);
    });
}

function applyBoaRowColours (row: IGenericTableRowData, bidColour: string, offerColour: string): void {
    const typeCell = row.row?.Type as ITableCellWithDefaults | undefined;
    if (!typeCell) return;

    const hasBidOverride = bidColour !== '' && bidColour !== DEFAULT_BID_COLOUR;
    const hasOfferOverride = offerColour !== '' && offerColour !== DEFAULT_OFFER_COLOUR;

    if (hasBidOverride || hasOfferOverride) {
        if (!('backgroundColourDefault' in typeCell)) {
            typeCell.backgroundColourDefault = typeCell.backgroundColour;
        }
    } else {
        typeCell.backgroundColour = typeCell.backgroundColourDefault || typeCell.backgroundColour;
    }

    if (hasBidOverride && typeCell.values?.[0] === 'B') {
        typeCell.backgroundColour = bidColour;
        return;
    }

    if (hasOfferOverride && typeCell.values?.[0] === 'O') {
        typeCell.backgroundColour = offerColour;
    }
}

function isTableHeader (header: IGenericTableHeaderRow | IGenericTableParentHeaderRow | undefined): header is IGenericTableHeaderRow {
    return !!header && 'isPlant' in header;
}

function findHeader (headerId: string, tableData: IGenericTableData): IGenericTableHeaderRow | null {
    const firstRowHeader = tableData.headerFirstRowById ? tableData.headerFirstRowById[headerId] : undefined;
    if (isTableHeader(firstRowHeader)) return firstRowHeader;

    return tableData.headerSecondRow?.find(header => header.id === headerId) ?? null;
}

function cellIsPlant (headerId: string, cell: IGenericTableRowFormatting, tableData: IGenericTableData): boolean {
    const overrideValue = cell.headerOverrides?.isPlant;
    if (overrideValue != null) return overrideValue;

    return findHeader(headerId, tableData)?.isPlant ?? false;
}

function removePlantColourOverrides (tableData: IGenericTableData): void {
    tableData.data.forEach((row) => {
        Object.keys(row.row ?? {}).forEach((headerId) => {
            const cell = row.row[headerId];
            if (!cell || !cellIsPlant(headerId, cell, tableData)) return;

            delete (cell as Partial<IGenericTableRowFormatting>).backgroundColour;
        });
    });
}

function addPlantColourOverrides (tableData: IGenericTableData, displaySettings: ILiveTableDisplaySettings): void {
    if (!displaySettings.selectedPlantGroupColourSettings.override) return;

    tableData.data.forEach((row) => {
        Object.keys(row.row ?? {}).forEach((headerId) => {
            const cell = row.row[headerId];
            if (!cell || !cellIsPlant(headerId, cell, tableData)) return;
            const plantId = cell.values[0];
            if (typeof plantId !== 'string' || !displaySettings.selectedPlantIds.includes(plantId)) return;

            cell.backgroundColour = displaySettings.selectedPlantGroupColourSettings.colour;
        });
    });
}

function findPriceMax (tableData: IGenericTableData): number {
    const max = tableData.data.reduce((currentMax, row) => Object.keys(row.row ?? {}).reduce((rowMax, key) => {
        const cell = row.row[key];
        if (cell?.cellType !== 'number' || !key.includes('Price')) return rowMax;

        const rawValue = cell.values?.[0];
        const parsedValue = typeof rawValue === 'number' ? rawValue : Number.parseFloat(String(rawValue));
        if (!Number.isFinite(parsedValue)) return rowMax;

        return Math.max(rowMax, parsedValue);
    }, currentMax), 0);

    return Number.isFinite(max) ? max : 0;
}

function getHeatmapColour (value: number, max: number, useDarkHeatmapColours: boolean): string | null {
    if (!Number.isFinite(value) || max <= 0) return null;

    const percent = Math.min(1, Math.max(0, value / max));
    let red = 0;
    let green = 0;
    let blue = 0;

    if (useDarkHeatmapColours) {
        if (percent < 0.5) {
            const progress = percent / 0.5;
            red = Math.round(51 + progress * (255 - 51));
            green = Math.round(51 + progress * (155 - 51));
            blue = Math.round(51 + progress * (0 - 51));
        } else {
            const progress = (percent - 0.5) / 0.5;
            red = 255;
            green = Math.round(155 - progress * 155);
            blue = 0;
        }
    } else if (percent < 0.5) {
        const progress = percent / 0.5;
        red = 255;
        green = Math.round(246 - progress * (246 - 224));
        blue = Math.round(204 - progress * (204 - 130));
    } else {
        const progress = (percent - 0.5) / 0.5;
        red = Math.round(255 - progress * (255 - 245));
        green = Math.round(224 - progress * (224 - 124));
        blue = Math.round(130 - progress * 130);
    }

    return `rgb(${red}, ${green}, ${blue})`;
}

function applyHeatmapColours (tableData: IGenericTableData, useDarkHeatmapColours: boolean): void {
    const max = findPriceMax(tableData);

    tableData.data.forEach((row) => {
        Object.keys(row.row ?? {}).forEach((key) => {
            const cell = row.row[key];
            if (!cell || cell.cellType !== 'number' || !key.includes('Price')) return;

            const rawValue = cell.values?.[0];
            const parsedValue = typeof rawValue === 'number' ? rawValue : Number.parseFloat(String(rawValue));
            const heatmapColour = getHeatmapColour(parsedValue, max, useDarkHeatmapColours);
            if (heatmapColour) {
                cell.backgroundColour = heatmapColour;
            }
        });
    });
}

export function applyLiveTableColours (
    tableData: IGenericTableData,
    moduleType: string,
    displaySettings: ILiveTableDisplaySettings,
): void {
    if (!tableData.data?.length) return;

    removePlantColourOverrides(tableData);

    if (moduleType === 'BmFeed' || moduleType === 'Notifications') {
        applyBmFeedOrNotificationsColours(tableData, moduleType, displaySettings);
    }

    if (moduleType === 'Boa') {
        applyBoaColours(tableData, displaySettings.bidColour, displaySettings.offerColour);
    }

    if (moduleType === 'JaoDailyAuctionsHeatMapTable' || moduleType === 'JaoIntradayAuctionsHeatMapTable') {
        applyHeatmapColours(tableData, displaySettings.useDarkHeatmapColours);
    }

    addPlantColourOverrides(tableData, displaySettings);
}

export function applyLiveTablePushDisplaySettings (
    changes: Array<ILiveTablePushChange>,
    moduleType: string,
    displaySettings: ILiveTableDisplaySettings,
): void {
    changes.forEach((change) => {
        if (change.changeType === 'Delete') return;
        if (!change.row) return;

        if (moduleType === 'BmFeed' || moduleType === 'Notifications') {
            applyBmFeedOrNotificationsRowColours(change.row, moduleType, displaySettings);
        }

        if (moduleType === 'Boa') {
            applyBoaRowColours(change.row, displaySettings.bidColour, displaySettings.offerColour);
        }
    });
}

function getTimezoneAdjustedCell (
    cell: IGenericTableRowFormatting,
    timeFormat: string,
    timezoneId: string,
): IGenericTableRowFormatting {
    if (cell.cellType !== 'date') return cell;

    const displayValues = cell.values.map(value => {
        if (typeof value !== 'number') return value;
        return formatGenericTableDateValue(value, cell.dateFormat, timeFormat, { timezoneId });
    });

    return {
        ...cell,
        displayValues,
    };
}

export function applyLiveTableTimezoneDisplay (
    tableData: IGenericTableData,
    timezoneId: string,
    timeFormat: string,
): IGenericTableData {
    return {
        ...tableData,
        data: tableData.data.map(row => ({
            ...row,
            row: Object.fromEntries(
                Object.entries(row.row).map(([key, cell]) => [
                    key,
                    getTimezoneAdjustedCell(cell, timeFormat, timezoneId),
                ]),
            ),
        })),
    };
}

export function trimTableDataToMaxEntries (tableData: IGenericTableData, maxEntriesOnTable?: number | null): void {
    if (!maxEntriesOnTable || maxEntriesOnTable < 0 || !tableData.headerToSort) return;

    while (tableData.data.length > maxEntriesOnTable) {
        const earliestIndex = findEarliestIndex(tableData.data, tableData.headerToSort);
        tableData.data.splice(earliestIndex, 1);
    }
}
